/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  filterBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';
import { classNames } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';

@classNames('layout-row', 'layout-align-start-center')
export default class AttendanceWidget extends Component {
  // Services
  @service
  attendance;

  @service
  attendanceCodes;

  @service
  dates;

  @service
  fetch;

  @service
  session;

  @service
  store;

  @service
  toaster;

  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  @computed('jurisdiction')
  get labelField() {
    switch (this.jurisdiction) {
      case Jurisdiction.AU_NSW:
        return 'label';
    }
    return 'reason';
  }

  // codeSet determines which code set to offer
  @tracked codeSet = 'quick';

  @computed('attendanceCodes.{allCodes.[],quickCodes.[]}', 'codeSet')
  get availableCodes() {
    switch (this.codeSet) {
      case 'quick':
        return filterBy(this.attendanceCodes.quickCodes, 'old', false);
      case 'full':
        return filterBy(this.attendanceCodes.allCodes, 'old', false);
    }
    // TODO: determine what to return if we land here.  Also, Jurisdictional code sets.
    return filterBy(this.attendanceCodes.quickCodes, 'old', false);
  }

  showLabel = true;

  @computed(
    'record.{code,hasMinorPartialAbsence}',
    'attendanceCodes.truancyClasses',
  )
  get codeClass() {
    let code = this.get('record.code');
    if (isEmpty(code)) {
      // Not marked - don't apply a class.
      return;
    }
    if (this.record.hasMinorPartialAbsence) {
      return 'attendance-late';
    }
    let codeClass = this.get(`attendanceCodes.truancyClasses.${code}`);
    if (!isEmpty(codeClass)) {
      return codeClass;
    }
    return 'attendance-marked';
  }

  @computed('record.{notes,code}', 'codeClass')
  get notesClass() {
    if (isEmpty(this.get('record.notes'))) {
      return;
    }

    if (isEmpty(this.get('record.code'))) {
      // We have notes, but no code.  Return the marked-but-not-present class.
      return 'attendance-marked';
    }

    return this.codeClass;
  }

  @observes('notesOpen')
  _watchNotes() {
    let record = this.record;
    let note = this.note;
    if (record) {
      // We have an existing record.
      if (this.notesOpen) {
        // Display existing notes when opening.
        this.note = record.get('notes');
      } else if (note !== record.get('notes')) {
        // Update existing record when saving.
        this.updateRecord(record.get('code'), note, 'Added note');
      }
    } else {
      if (this.notesOpen) {
        this.note = '';
      } else if (!isEmpty(note)) {
        this.createRecord(this.period, { code: '' }, note)
          .then((record) =>
            record.save().then(() => {
              this.record = record;
              this.toaster.success('Attendance notes saved.');

              this.onSave();
            }),
          )
          .catch(() => {
            this.toaster.error('Error saving attendance notes!');
          });
      }
    }
  }

  // Change / audit details.
  @tracked
  reason = '';

  @action
  setReason(value) {
    this.reason = value;
  }

  @tracked
  effectiveFrom = '';

  @action
  setEffectiveFrom(value) {
    this.effectiveFrom = value;
  }

  @tracked
  effectiveFromDefaulted = false;

  @tracked
  note;

  @action
  setNote(value) {
    this.note = value;
  }

  @tracked
  reasonOpen;

  @tracked
  newCode;

  @tracked
  record;

  @computed('record.period')
  get minTime() {
    if (!isEmpty(this.record.period)) {
      return this.get('record.period.startTime');
    }
    return '08:30';
  }

  @computed('record.date', 'minTime')
  get minMoment() {
    return moment(
      `${this.get('record.date')}T${this.minTime}`,
      'YYYY-MM-DDTHH:mm',
    );
  }

  @computed('record.period')
  get maxTime() {
    if (!isEmpty(this.record.period)) {
      return this.get('record.period.endTime');
    }
    return '15:30';
  }

  @computed('record.date', 'maxTime')
  get maxMoment() {
    return moment(
      `${this.get('record.date')}T${this.maxTime}`,
      'YYYY-MM-DDTHH:mm',
    );
  }

  @computed('record.date', 'minTime', 'maxTime')
  get effectiveFromValidation() {
    let minTime = this.minMoment.format('h:mm A');
    let maxTime = this.maxMoment.format('h:mm A');
    return [
      {
        message: `Time must be within the school period (${minTime} - ${maxTime}).`,
        validate: (val) => {
          if (isEmpty(val)) {
            return true;
          }
          let parsed = moment(
            `${this.get('record.date')}T${val}`,
            'YYYY-MM-DDTHH:mm',
          );
          return (
            parsed.isValid() &&
            parsed.isSameOrAfter(this.minMoment, 'm') &&
            parsed.isSameOrBefore(this.maxMoment, 'm')
          );
        },
      },
    ];
  }

  // onSave is called after the record is saved, so that the caller can specify follow-up actions.
  onSave() {}

  // emptyOrUnknown determines whether a given code is either empty or unknown.
  emptyOrUnknown(code) {
    return isEmpty(code) || code === this.get('attendanceCodes.unknown.code');
  }

  @action
  async setStatus(status) {
    let record = this.record;
    if (record) {
      let oldCode = record.get('code');

      if (oldCode !== status.code && !this.emptyOrUnknown(oldCode)) {
        // Changing from a non-empty, known code - this requires an audit.
        return this.triggerAudit(record, status);
      } else {
        let effectiveFrom;
        let reason = '';
        if (this.trackMovements) {
          // If we're moving from blank/unknown, update the latest movement.
          effectiveFrom = record.get('movements.lastObject.time');
          // Push existing notes into reason, so they stay on the movement.
          reason = record.get('notes');
          // Unless we're moving _to_ Present.
          if (status.code === this.get('attendanceCodes.present.code')) {
            // Assume a late arrival, being marked at time of arrival.
            // Request a reason and time for confirmation, defaulting to now.
            return this.triggerAudit(record, status);
          }
        }
        this.updateRecord(
          status.code,
          record.get('notes'),
          reason,
          effectiveFrom,
        );
      }
    } else {
      record = await this.createRecord(this.period, status);

      record
        .save()
        .then(() => {
          this.record = record;
          this.toaster.success('Attendance data saved.');

          this.onSave();
        })
        .catch(() => {
          this.toaster.error('Error saving attendance data!');
        });
    }
  }

  @action
  clearStatus() {
    this.newCode = null;
    this.reason = '';
    this.reasonOpen = true;
  }

  @action
  toggle() {
    this.toggleProperty('reasonOpen');
  }

  @action
  clear() {
    this.reason = '';
  }

  @action
  save() {
    this.reasonOpen = false;
    if (!isEmpty(this.newCode)) {
      let effectiveFrom;
      if (!isEmpty(this.effectiveFrom)) {
        effectiveFrom = moment(
          `${this.get('record.date')}T${this.effectiveFrom}`,
          'YYYY-MM-DDTHH:mm',
        ).unix();
      }
      let notes = this.trackMovements ? this.reason : this.get('record.notes');
      this.updateRecord(this.newCode, notes, this.reason, effectiveFrom);
    } else if (this.trackMovements && this.get('record.movements.length') > 1) {
      // If we have more than one movement for this period already, only
      // clear the latest movement.
      let movements = sortBy(this.get('record.movements'), 'time');
      let lastMovement = movements.pop();
      let prevMovement = movements.at(-1);
      this.record.setProperties({
        effectiveFrom: lastMovement.time,
        code: prevMovement.code,
        notes: prevMovement.notes,
        reason: this.reason,
        movements,
      });
      this.record
        .save()
        .then(() => {
          this.toaster.success('Attendance data saved.');
          this.onSave();
        })
        .catch(() => {
          this.toaster.error('Error saving attendance data!');
        });
    } else {
      // No movement tracking, or only one movement.
      this.deleteRecord(this.reason);
    }
  }

  // createRecord gives a promise, because a belongsTo takes a record, not the
  // async proxy that callers can give as person.
  async createRecord(period, status, notes) {
    return this.attendance.createAttendanceRecord(
      await this.person,
      this.date,
      period,
      status,
      notes,
    );
  }

  updateRecord(code, notes, reason, effectiveFrom, record) {
    if (isEmpty(record)) {
      record = this.record;
    }
    record = this.attendance.updateAttendanceRecord(
      code,
      notes,
      reason,
      record,
      effectiveFrom,
    );

    return record
      .save()
      .then(() => {
        this.toaster.success('Attendance data saved.');

        this.onSave();
      })
      .catch(() => {
        this.toaster.error('Error saving attendance data!');
      });
  }

  triggerAudit(record, status) {
    // effectiveFrom defaults to empty if the record isn't for today,
    // or if it's an NZ MoE school.
    let effectiveFrom = '';
    if (this.trackMovements) {
      if (record.date === this.dates.currentDate) {
        // If it's today, default to the current time.
        effectiveFrom = moment().format('HH:mm');
      } else {
        // Otherwise, default to the period's start time.
        effectiveFrom = record.get('period.startTime');
      }
    }
    this.newCode = status.code;
    this.reason = '';
    this.effectiveFrom = effectiveFrom;
    this.effectiveFromDefaulted = !isEmpty(effectiveFrom);
    this.reasonOpen = true;
  }

  deleteRecord(reason) {
    this.record.setProperties({
      authorId: this.get('session.person'),
      code: '',
      note: '',
      reason,
    });
    this.record.set('auditReason', reason);
    this.record
      .destroyRecord()
      .then(() => {
        this.toaster.success('Attendance data deleted.');
        this.onSave();
      })
      .catch(() => {
        this.toaster.error('Error saving attendance data!');
      });
  }
}
